import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getImageUrl } from '../api/api';
import './Services.css';

const DEFAULT_DATA = {
  heroTitle: '',
  heroSubtitle: '',
  heroImage: '',
  introTitle: '',
  introText: '',
  rentalPlans: [],
  fleet: [],
  howItWorks: [],
  includes: [],
  requirements: [],
  faq: [],
  ctaTitle: '',
  ctaSubtitle: '',
  ctaPhone: '',
  ctaButtonText: '',
  ctaButtonLink: '/contact',
};

function Services() {
  const navigate = useNavigate();
  const [data, setData] = useState(DEFAULT_DATA);
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    api.get('/services?t=' + Date.now()).then(({ data: d }) => {
      if (d) setData(prev => ({ ...prev, ...d }));
    }).catch(() => {});
  }, []);

  return (
    <div className="services-page">
      {/* Hero */}
      {data.heroTitle && (
      <div className="services-hero" style={data.heroImage ? { backgroundImage: `linear-gradient(rgba(10,46,26,0.85), rgba(0,105,92,0.78)), url(${getImageUrl(data.heroImage)})` } : {}}>
        <div className="container">
          <span className="services-hero-badge">🚲 Rentals Available</span>
          <h1>{data.heroTitle}</h1>
          <p>{data.heroSubtitle}</p>
        </div>
      </div>
      )}

      {/* Intro */}
      {data.introTitle && (
      <div className="container">
        <div className="services-intro">
          <h2>{data.introTitle}</h2>
          <p>{data.introText}</p>
        </div>
      </div>
      )}

      {/* Pricing Plans */}
      {data.showRentalPlans !== false && data.rentalPlans?.length > 0 && (
      <div className="services-pricing-section">
        <div className="container">
          <h2 className="section-title">Rental Plans</h2>
          <div className="pricing-grid">
            {data.rentalPlans.map((plan, i) => (
              <div key={i} className={`pricing-card${plan.popular ? ' pricing-popular' : ''}${selectedPlan === i ? ' pricing-selected' : ''}`} onClick={() => setSelectedPlan(selectedPlan === i ? null : i)} style={{ cursor: 'pointer' }}>
                {plan.popular && <span className="pricing-badge">Most Popular</span>}
                <h3>{plan.name}</h3>
                <div className="pricing-price">{plan.price}</div>
                <div className="pricing-duration">{plan.duration}</div>
                <p>{plan.description}</p>
                {selectedPlan === i && (
                  <button className="btn" style={{ marginTop: 12, width: '100%', padding: '12px', fontSize: 14, fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); navigate(`/rental-booking?plan=${encodeURIComponent(plan.name)}&price=${encodeURIComponent(plan.price)}`); }}>
                    Book Now →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* How It Works */}
      {data.showHowItWorks !== false && data.howItWorks?.length > 0 && (
      <div className="container">
        <h2 className="section-title">How It Works</h2>
        <div className="steps-grid">
          {data.howItWorks.map((step, i) => (
            <div key={i} className="step-card">
              <div className="step-number">{step.step}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Fleet */}
      {data.showFleet !== false && data.fleet?.length > 0 && (
      <div className="services-fleet-section">
        <div className="container">
          <h2 className="section-title">Our Fleet</h2>
          <div className="fleet-grid">
            {data.fleet.map((bike, i) => (
              <div key={i} className="fleet-card">
                {bike.image ? (
                  <div className="fleet-card-img">
                    <img src={getImageUrl(bike.image)} alt={bike.name} />
                  </div>
                ) : (
                  <div className="fleet-card-placeholder">🚲</div>
                )}
                <div className="fleet-card-body">
                  <h3>{bike.name}</h3>
                  <p>{bike.description}</p>
                  {bike.specs && <span className="fleet-specs">{bike.specs}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* What's Included & Requirements */}
      {((data.showIncludes !== false && data.includes?.length > 0) || (data.showRequirements !== false && data.requirements?.length > 0)) && (
      <div className="container">
        <div className="info-columns">
          {data.showIncludes !== false && data.includes?.length > 0 && (
          <div className="info-col">
            <h3>✅ What's Included</h3>
            <ul>
              {data.includes.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
          )}
          {data.showRequirements !== false && data.requirements?.length > 0 && (
          <div className="info-col">
            <h3>📋 Requirements</h3>
            <ul>
              {data.requirements.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
          )}
        </div>
      </div>
      )}

      {/* FAQ */}
      {data.showFaq !== false && data.faq?.length > 0 && (
      <div className="services-faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            {data.faq.map((item, i) => (
              <div key={i} className={`faq-item${openFaq === i ? ' faq-open' : ''}`}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{item.question}</span>
                  <span className="faq-toggle">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <div className="faq-answer"><p>{item.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* CTA */}
      {data.showCta !== false && data.ctaTitle && (
      <div className="services-cta">
        <div className="container">
          <h2>{data.ctaTitle}</h2>
          <p>{data.ctaSubtitle}</p>
          <div className="services-cta-btns">
            {data.ctaPhone && <a href={`tel:${data.ctaPhone}`} className="btn">📞 {data.ctaPhone}</a>}
            <button className="btn services-btn-outline" onClick={() => {
              const plan = data.rentalPlans?.[0];
              if (plan) navigate(`/rental-booking?plan=${encodeURIComponent(plan.name)}&price=${encodeURIComponent(plan.price)}`);
              else navigate('/rental-booking');
            }}>{data.ctaButtonText || 'Book Now'}</button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default Services;
